import Input from "shared/Input/Input";
import React, { useState, ChangeEvent, FC, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import { useData } from "data/data-provider";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { ProfileChangeFun } from "api/profile/profile";
import toast from "react-hot-toast";

interface RenterInfoProp {
  bookingDetails: any;
  setIsRenterInfoEdit: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccessfulUpdate?: any;
}

const RenterInfo: FC<RenterInfoProp> = ({
  bookingDetails,
  setIsRenterInfoEdit,
  onSuccessfulUpdate,
}) => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    phone,
    setPhone,
  } = useData();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const clearError = (field: string) => {
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[field];
      return updatedErrors;
    });
  };

  const isFormValid = () => {
    if (!firstName) return false;
    if (!lastName) return false;
    if (!email) return false;
    const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    if (!emailRegex.test(email)) return false;
    const numericPhone = phone ? phone.replace(/\D/g, "") : "";
    if (!numericPhone || numericPhone.length < 10) return false;
    return true;
  };

  useEffect(() => {
    setFirstName(bookingDetails?.customer?.first_name);
    setLastName(bookingDetails?.customer?.last_name);
    setEmail(bookingDetails?.customer?.email);
    const rawPhone = bookingDetails?.customer?.phone_no || "";
    if (rawPhone) {
      let formattedPhone = rawPhone.startsWith("+") ? rawPhone : `+${rawPhone}`;
      setPhone(formattedPhone);
    }
  }, []);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!phone) {
      newErrors.phone = "Phone number is required";
    } else if (phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const RenterInfoUpdate = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    const payload = {
      booking_uid: bookingDetails?.unique_identifier,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_no: phone.replace(/\D/g, ""),
    };
    try {
      const result = await ProfileChangeFun(payload);
      if (!result.error) {
        toast.success("Renter Info is updated successfully");
        setLoading(false);
        setIsRenterInfoEdit(false);
        if (onSuccessfulUpdate) {
          onSuccessfulUpdate();
        }
      } else {
        toast.error(result.error);
        setLoading(false);
      }
    } catch (err) {
      toast.error("Couldn't save changes. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col sm:rounded-2xl sm:space-y-8 pb-10 px-0 sm:p-4 xl:p-8 !space-y-6 mt-4">
      {" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="border border-secondary-100 p-2 rounded-md">
            <span className="my-2 text-sm">First Name</span>
          <Input
              placeholder=""
            value={firstName}
              className="border-none !p-0 focus:outline-none focus:ring-0"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFirstName(e.target.value);
                if (e.target.value) clearError("firstName");
            }}
          />
          </div>
          {errors.firstName && (
            <span className="text-red-500 text-xs font-semibold">
              {errors.firstName}
            </span>
          )}
        </div>
        <div>
          <div className="border border-secondary-100 p-2 rounded-md">
            <span className="my-2 text-sm">Last Name</span>
          <Input
              placeholder=""
            value={lastName}
              className="border-none !p-0 focus:outline-none focus:ring-0"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setLastName(e.target.value);
              if (e.target.value) clearError("lastName");
            }}
          />
          </div>
          {errors.lastName && (
            <span className="text-red-500 text-xs font-semibold">
              {errors.lastName}
            </span>
          )}
        </div>
        <div>
          <div className="border border-secondary-100 p-2 rounded-md">
            <span className="my-2 text-sm">Email</span>
          <Input
              placeholder=""
            type="email"
              className="border-none !p-0 focus:outline-none focus:ring-0"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              if (
                /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(
                  e.target.value
                )
              ) {
                clearError("email");
              }
            }}
          />
          </div>
          {errors.email && (
            <span className="text-red-500 text-xs font-semibold">
              {errors.email}
            </span>
          )}
        </div>
        <div>
          <div className="border border-secondary-100 p-2 rounded-md">
            <span className="my-2 text-sm">Phone</span>
          <PhoneInput
            country={"us"}
            value={phone}
            onChange={(phone) => {
              setPhone(phone);
              if (phone.length >= 10) clearError("phone");
            }}
            countryCodeEditable={false}
            disableDropdown={false}
            inputProps={{
              name: "phone",
              required: true,
              autoFocus: false,
            }}
            inputStyle={{
              border: "none",
              width: "100%",
              minHeight: "44px",
              boxShadow: "none",
                background: "transparent",
            }}
            buttonStyle={{
              background: "transparent",
              border: "none",
            }}
          />
          </div>
          {errors.phone && (
            <span className="text-red-500 text-xs font-semibold">
              {errors.phone}
            </span>
          )}
        </div>
      </div>
      <div className="flex justify-center">
        <ButtonPrimary
          className={`min-w-36 !rounded-md !border-none ${
            (!isFormValid() || loading) ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={RenterInfoUpdate}
          disabled={!isFormValid() || loading}
        >
          {loading ? (
            <svg
              aria-hidden="true"
              className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : (
            <>Update</>
          )}
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default RenterInfo;
